import React, { useCallback, useState } from "react";
import { Alert, AlertTitle, Box, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import {
  lintPromptText,
  severityForFinding,
} from "services/llm/promptLintService";

/**
 * "Check wording" for prompt authors.
 *
 * The people writing organization rules are clinicians and operations leads, not
 * prompt engineers, and the constructions that trip models up are exactly the
 * ones that read as careful English — "do not omit", "not uncommon", "never
 * document X, Y and Z without a date". The benchmark put an 8B reader at 50%
 * on that last shape. This surfaces it while the rule is still editable.
 *
 * Advisory only: nothing here blocks a save or rewrites what the author typed.
 */

const PromptLintPanel = ({ getText, label = "Check wording" }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const runLint = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const text = typeof getText === "function" ? getText() : "";
      setResult(await lintPromptText({ text }));
    } catch (err) {
      setError(err?.message || "Could not check this text.");
    } finally {
      setLoading(false);
    }
  }, [getText]);

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Box>
        <LoadingButton
          variant="outlined"
          loading={loading}
          startIcon={<FactCheckOutlined />}
          onClick={runLint}
        >
          {label}
        </LoadingButton>
      </Box>

      {error ? (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {result && result.ok ? (
        <Alert severity="success" onClose={() => setResult(null)}>
          No risky negation found
          {result.structure?.directiveCount
            ? ` — the route reads this as ${result.structure.directiveCount} rule${
                result.structure.directiveCount === 1 ? "" : "s"
              }.`
            : "."}
        </Alert>
      ) : null}

      {result && !result.ok ? (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            {result.findings.length} thing
            {result.findings.length === 1 ? "" : "s"} worth rewording. These are
            suggestions — your text is saved exactly as you wrote it.
          </Typography>
          {result.findings.map((finding, index) => (
            <Alert
              key={`${finding.rule}-${finding.line}-${index}`}
              severity={severityForFinding(finding)}
            >
              <AlertTitle>
                {finding.label} (line {finding.line})
              </AlertTitle>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", mb: 0.5 }}
              >
                {finding.excerpt}
              </Typography>
              <Typography variant="body2">{finding.message}</Typography>
              <Typography variant="body2" color="text.secondary">
                {finding.suggestion}
              </Typography>
            </Alert>
          ))}
          {result.truncated ? (
            <Typography variant="body2" color="text.secondary">
              More findings were hidden. Fix these and check again.
            </Typography>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
};

export default PromptLintPanel;
